import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check, Clock, MapPin, Award, Users } from "lucide-react";
import { getClassBySlug, IMAGES, priceLabel } from "@/data/classes";
import Seo, { courseJsonLd } from "@/components/Seo";

export default function ClassDetail() {
  const { slug } = useParams();
  const c = getClassBySlug(slug);
  if (!c) return <Navigate to="/classes" replace />;

  return (
    <div>
      <Seo
        title={c.name}
        path={`/classes/${c.slug}`}
        description={`${c.tagline} ${c.duration} · ${c.audience}`}
        type="article"
        jsonLd={courseJsonLd(c)}
      />
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="font-mono-label text-[#d90429] mb-4">{c.code}</div>
            <h1 className="font-display text-5xl md:text-6xl font-black text-[#0a0a0a] leading-none">
              {c.name}
            </h1>
            <p className="mt-6 text-xl text-gray-700 leading-relaxed">{c.tagline}</p>
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              <div className="p-5 border border-gray-200 rounded-md">
                <div className="font-mono-label text-gray-500 mb-1">Duration</div>
                <div className="font-display text-lg font-bold text-[#0a0a0a]">{c.duration}</div>
              </div>
              <div className="p-5 border border-gray-200 rounded-md">
                <div className="font-mono-label text-gray-500 mb-1">Format</div>
                <div className="font-display text-lg font-bold text-[#0a0a0a]">In-Person</div>
              </div>
              <div className="p-5 border border-gray-200 rounded-md">
                <div className="font-mono-label text-gray-500 mb-1">Price</div>
                {c.variants && c.variants.length > 0 ? (
                  <div className="space-y-1" data-testid={`class-price-variants-${c.slug}`}>
                    {c.variants.map((v) => (
                      <div key={v.label} className="flex items-baseline justify-between gap-3">
                        <span className="text-xs font-mono-label text-gray-500">{v.label}</span>
                        <span className="font-display text-lg font-bold text-[#0a0a0a]">${v.price}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="font-display text-lg font-bold text-[#0a0a0a]">
                    {priceLabel(c)}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to={`/schedule?class=${c.slug}`}
                data-testid="class-detail-signup-cta"
                className="inline-flex items-center gap-2 rounded-md bg-[#d90429] px-8 py-4 text-base font-bold text-white hover:bg-black transition-colors"
              >
                Sign Up for {c.code} <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                data-testid="class-detail-ask-question"
                className="inline-flex items-center gap-2 rounded-md border-2 border-[#0a0a0a] px-8 py-4 text-base font-bold text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors"
              >
                Ask a Question
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img
              src={IMAGES[c.imageKey]}
              alt={c.name}
              className="w-full h-80 lg:h-full object-cover rounded-lg border border-gray-200"
            />
          </div>
        </div>
      </section>

      <section className="bg-[#f9fafb] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="font-mono-label text-gray-500 mb-3">What You&apos;ll Cover</div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#0a0a0a]">Course Highlights</h2>
            <ul className="mt-8 space-y-4">
              {c.highlights.map((h, i) => (
                <li key={i} className="flex gap-3">
                  <Check className="w-6 h-6 text-[#d90429] shrink-0 mt-0.5" />
                  <span className="text-gray-800 text-lg">{h}</span>
                </li>
              ))}
            </ul>

            <div className="font-mono-label text-gray-500 mt-14 mb-3">Learning Outcomes</div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#0a0a0a]">
              You will leave able to
            </h2>
            <ul className="mt-8 space-y-4">
              {c.outcomes.map((h, i) => (
                <li key={i} className="flex gap-3">
                  <Check className="w-6 h-6 text-[#d90429] shrink-0 mt-0.5" />
                  <span className="text-gray-800 text-lg">{h}</span>
                </li>
              ))}
            </ul>
          </div>
          <aside className="lg:col-span-5">
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <div className="font-mono-label text-[#d90429] mb-3">Who It&apos;s For</div>
              <p className="text-gray-800">{c.audience}</p>

              <div className="font-mono-label text-[#d90429] mt-8 mb-3">Certification</div>
              <div className="flex gap-3 items-start">
                <Award className="w-6 h-6 text-[#d90429] shrink-0" />
                <p className="text-gray-800">{c.certification}</p>
              </div>

              <div className="font-mono-label text-[#d90429] mt-8 mb-3">Format & Location</div>
              <div className="flex gap-3 items-start">
                <MapPin className="w-6 h-6 text-[#d90429] shrink-0" />
                <p className="text-gray-800">{c.format}</p>
              </div>

              <div className="mt-8 border-t border-gray-200 pt-6">
                <Link
                  to={`/schedule?class=${c.slug}`}
                  className="inline-flex items-center justify-center w-full gap-2 rounded-md bg-[#d90429] px-6 py-4 text-base font-bold text-white hover:bg-black transition-colors"
                  data-testid="class-detail-sidebar-signup"
                >
                  Book This Class <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>

            <div className="mt-6 p-6 bg-[#0a0a0a] text-white rounded-lg">
              <div className="flex items-center gap-3">
                <Users className="w-6 h-6 text-[#d90429]" />
                <div className="font-display font-bold text-lg">Training a group?</div>
              </div>
              <p className="mt-3 text-gray-300 text-sm leading-relaxed">
                We come on-site anywhere in DFW with group pricing.
              </p>
              <Link
                to="/group-training"
                className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-white hover:text-[#d90429]"
                data-testid="class-detail-group-link"
              >
                Group Training Details <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
